// The company's rules as rows any Setup page shows (RULE_SECTIONS): each section its rules and the fixed
// ones, each rule its control with its own Save, and a department's own value where the setting allows
// one. Riverstone's Rules & defaults draws its groups with the same rows.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { Loading } from '../shared/Loading.tsx';
import {
  centsToDollars,
  dollarsToCents,
  hoursAsMinutes,
  minutesAsHours,
  OVERTIME_PARTS,
  TWO_FACTOR_MODES,
  TWO_FACTOR_ROLES,
  wordsOf,
  type AnswerHours,
  type Overtime,
  type TwoFactor,
  ruleEditable,
  type Rights,
  ruleSection,
  rulesCardLine,
  sectionsOf,
  settingWords,
  SIGN_IN_METHODS,
  toggleMethod,
  WEEKDAYS,
  type Control,
  type FixedRule,
  type Rule,
  type RulePage,
  type SettingRow,
} from './settings-model.ts';
import { FinancingPlans } from './FinancingPlans.tsx';
import { useFoundSetting, useRuleData, type RuleData, type StaffName, type Unit } from './rule-data.ts';
import { useDesign } from '../blueprint/design.ts';
import type { FinancingSetting } from './financing-model.ts';
// the rows' own styles, for a page outside Settings that shows rules (Company details)
import './settings.css';
import '../blueprint/setup-settings.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export { useRuleData, type RuleData, type StaffName, type Unit } from './rule-data.ts';

/** A section's rows: its rules (those the server has), then the rules the product fixes. */
export function RuleRows({
  rules,
  fixed,
  data,
  rights,
}: {
  rules: readonly Rule[];
  fixed?: readonly FixedRule[] | undefined;
  data: RuleData;
  rights: Rights;
}) {
  const byKey = new Map((data.rows ?? []).map((r) => [r.key, r]));
  const bp = useDesign() === 'blueprint';
  // In Blueprint a rule that only matters while another is on sits under it, and only while it's on
  const parentOf = (rule: Rule) =>
    bp && rule.under && rules.some((r) => r.key === rule.under) ? rule.under : null;
  return (
    <ul className="rules">
      {rules.map((rule) => {
        const row = byKey.get(rule.key);
        const parent = parentOf(rule);
        if (parent && byKey.get(parent)?.value !== true) return null;
        return row ? (
          <RuleRow
            key={rule.key}
            rule={rule}
            row={row}
            units={data.units}
            staff={data.staff}
            canEdit={ruleEditable(rule, rights, row)}
            onSaved={data.load}
            sub={!!parent}
            warning={rule.warn?.(row.value, (k) => byKey.get(k)?.value) ?? null}
          />
        ) : null;
      })}
      {fixed?.map((f) => (
        <li key={f.label} className="rule">
          <div className="what">
            <b>{f.label}</b>
            {f.help && <p className="muted small">{f.help}</p>}
          </div>
          <span className="chip say">Always</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A Setup page's rule sections (RULE_SECTIONS), before its own lists (`top`) or after them. A section
 * with no rules draws nothing; `inside` puts a page's own part in a section, under its rules (Document
 * numbers in Signatures and numbers), or a part made from the section's settings as they are now (the
 * price book's pictures, which follow their switch).
 */
export function RuleSections({
  page,
  top = false,
  rights,
  inside = {},
}: {
  page: RulePage;
  top?: boolean;
  rights: Rights;
  inside?: Record<string, ReactNode | ((rows: readonly SettingRow[]) => ReactNode)>;
}) {
  const sections = sectionsOf(page, top).filter(
    (s) => s.rules.length > 0 || !!s.fixed?.length || inside[s.id] !== undefined,
  );
  const data = useRuleData(sections.length > 0);
  const bp = useDesign() === 'blueprint';
  if (!sections.length) return null;
  if (data.error) return <p className="error">{data.error}</p>;
  if (!data.rows) return <Loading error={null} what="the rules" retry={() => void data.load()} />;
  return (
    <>
      {sections.map((s) => {
        const part = inside[s.id];
        return (
          <section key={s.id} className="sheet card" aria-label={s.title}>
            <h2>{s.title}</h2>
            {bp && s.line && <p className="muted bp-line">{s.line}</p>}
            {rulesCardLine(s.rules, rights, data.rows) && (
              <p className="muted small read-only">{rulesCardLine(s.rules, rights, data.rows)}</p>
            )}
            {(s.rules.length > 0 || !!s.fixed?.length) && (
              <RuleRows rules={s.rules} fixed={s.fixed} data={data} rights={rights} />
            )}
            {typeof part === 'function' ? part(data.rows ?? []) : part}
          </section>
        );
      })}
    </>
  );
}

/** One section's rows alone, inside a page's own list of the same name (RuleSection.inPart). */
export function SectionRules({ id, rights }: { id: string; rights: Rights }) {
  const s = ruleSection(id);
  const data = useRuleData(!!s);
  if (!s || data.error || !data.rows) return null;
  return <RuleRows rules={s.rules} fixed={s.fixed} data={data} rights={rights} />;
}

export function RuleRow({
  rule,
  row,
  units,
  staff,
  canEdit,
  onSaved,
  sub = false,
  warning = null,
}: {
  rule: Rule;
  row: SettingRow;
  units: Unit[];
  staff: StaffName[];
  canEdit: boolean;
  onSaved: () => Promise<void>;
  /** Blueprint: it sits under the rule it depends on, indented. */
  sub?: boolean;
  /** A plain warning beside it for its saved value (Rule.warn); it never stops the choice. */
  warning?: string | null;
}) {
  const bp = useDesign() === 'blueprint';
  const perUnit = row.scopes.includes('business_unit');
  const found = useFound(rule.key);
  // Overtime's three thresholds and two-factor's roles are too wide to sit beside the label in
  // Blueprint's sheet: the label column squeezed to a word a line, so they go under it instead. A
  // paragraph (a text's words) reads as words across the row, not a box squeezed at its end.
  const wide = bp && ['overtime', 'twoFactor', 'answerHours', 'paragraph'].includes(rule.control.kind);
  const cls = ['rule', wide && 'wide', sub && 'sub', found.on && 'found'].filter(Boolean).join(' ');
  // Blueprint names a department's own value only where there is one: one by name, more as a count
  const differ = row.overrides.filter((o) => o.scopeType === 'business_unit');
  const one = differ.length === 1 ? units.find((u) => u.id === differ[0]!.scopeId) : undefined;
  const differWords = one
    ? `${one.name}: ${settingWords(rule.control, differ[0]!.value, staff)}`
    : differ.length > 1
      ? `${differ.length} departments differ`
      : 'Set for one department';
  return (
    <li className={cls} ref={found.ref}>
      <div className="what">
        <b>{rule.label}</b>
        {rule.ownerOnly && <span className="chip say">Owner only</span>}
        {rule.help && <p className="muted small">{rule.help}</p>}
        {warning && (
          <p className="warn-text small" role="status">
            {warning}
          </p>
        )}
      </div>
      <Editor
        id={`set-${rule.key}`}
        label={rule.label}
        control={rule.control}
        value={row.value}
        staff={staff}
        disabled={!canEdit}
        auto={bp}
        confirm={rule.confirm}
        onSave={async (value) => {
          await client.call('settings.set', { key: rule.key, value });
          await onSaved();
        }}
      />
      {/* under its control, as the prototype draws it: whether the value is still the default */}
      {bp && <RowSource rule={rule} row={row} canEdit={canEdit} onSaved={onSaved} />}
      {perUnit && units.length > 0 && (!bp || canEdit || differ.length > 0) && (
        <details className="perunit">
          <summary>
            {bp
              ? differWords
              : `Per department${row.overrides.length ? ` (${row.overrides.length} set)` : ''}`}
          </summary>
          <ul className="rows">
            {units.map((u) => {
              const o = row.overrides.find((x) => x.scopeType === 'business_unit' && x.scopeId === u.id);
              return (
                <li key={u.id} className="mat">
                  <span>
                    {u.name}
                    {!o && <span className="muted small"> · uses the company value</span>}
                  </span>
                  <Editor
                    id={`set-${rule.key}-${u.id}`}
                    label={`${rule.label} for ${u.name}`}
                    control={rule.control}
                    value={o ? o.value : row.value}
                    staff={staff}
                    disabled={!canEdit}
                    auto={bp}
                    confirm={rule.confirm}
                    onSave={async (value) => {
                      await client.call('settings.set', {
                        key: rule.key,
                        scopeType: 'business_unit',
                        scopeId: u.id,
                        value,
                      });
                      await onSaved();
                    }}
                    onClear={
                      o && canEdit
                        ? async () => {
                            await client.call('settings.clear', {
                              key: rule.key,
                              scopeType: 'business_unit',
                              scopeId: u.id,
                            });
                            await onSaved();
                          }
                        : undefined
                    }
                  />
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </li>
  );
}

/**
 * One value's control, with its own Save once it changes (and Use company value for an override). In
 * Blueprint (`auto`) a change saves itself: a choice at once, typing when it's left or on Enter, and a
 * change with consequences says what it does first.
 */
function Editor({
  id,
  label,
  control,
  value,
  staff,
  disabled,
  auto = false,
  confirm,
  onSave,
  onClear,
}: {
  id: string;
  label: string;
  control: Control;
  value: unknown;
  staff: StaffName[];
  disabled: boolean;
  auto?: boolean;
  confirm?: Rule['confirm'];
  onSave: (value: unknown) => Promise<void>;
  onClear?: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<unknown>(value);
  // what a typed control holds as text: dollars, a list of words, the overtime hours
  const asText = () =>
    control.kind === 'money'
      ? value === null || value === undefined
        ? ''
        : centsToDollars(Number(value))
      : control.kind === 'words'
        ? (Array.isArray(value) ? (value as string[]) : []).join(control.perLine ? '\n' : ', ')
        : '';
  const asHours = () =>
    control.kind === 'overtime'
      ? Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, minutesAsHours((value as Overtime)[k])]))
      : {};
  const [text, setText] = useState(asText);
  const [hours, setHours] = useState<Record<string, string>>(asHours);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [asking, setAsking] = useState<{ value: unknown; words: string } | null>(null);
  // the value last sent, so leaving the field after a choice saved it doesn't send it again
  const sent = useRef<string | null>(null);
  useEffect(() => {
    setDraft(value);
    setText(asText());
    setHours(asHours());
    sent.current = null;
    // again only when the saved value or the control changes
  }, [value, control.kind]);
  const overtime = (): Overtime | null => {
    const o = Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, hoursAsMinutes(hours[k] ?? '')]));
    return Object.values(o).some((m) => m === null) ? null : (o as unknown as Overtime);
  };
  // a money field that may be left empty (the ad cap) saves empty as none
  const empty = control.kind === 'money' && !!control.none && text.trim() === '';
  const out =
    control.kind === 'money'
      ? empty
        ? null
        : dollarsToCents(text)
      : control.kind === 'words'
        ? wordsOf(text, control.perLine)
        : control.kind === 'overtime'
          ? overtime()
          : draft;
  const dirty = JSON.stringify(out) !== JSON.stringify(value);
  // back to the saved value: a refusal of another value no longer applies (QA-560)
  useEffect(() => {
    if (!dirty) setError(null);
  }, [dirty]);
  const save = async (v: unknown = out) => {
    setSaved(false); // a refusal never sits beside an earlier "Saved" (QA-560)
    if (control.kind === 'money' && v === null && !empty) return setError('Enter an amount like 89.00.');
    if (control.kind === 'overtime' && v === null) return setError('Enter each as hours, like 8 or 7.5.');
    if (control.kind === 'paragraph') {
      const missing = control.keep?.find((k) => !String(v).includes(k));
      if (missing) return setError(`Keep ${missing} in it: it is filled in for each customer.`);
    }
    setBusy(true);
    try {
      sent.current = JSON.stringify(v);
      await onSave(v);
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      sent.current = null;
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  // Blueprint: send a changed value now, or first say what it does
  const commit = (v: unknown) => {
    const j = JSON.stringify(v);
    if (!auto || busy || j === JSON.stringify(value) || j === sent.current) return;
    const words = confirm?.(v, value) ?? null;
    if (words) return setAsking({ value: v, words });
    void save(v);
  };
  // a choice: shown at once, and in Blueprint saved at once
  const pick = (v: unknown) => {
    setDraft(v);
    commit(v);
  };
  const putBack = () => {
    setAsking(null);
    setDraft(value);
    setText(asText());
    setHours(asHours());
  };
  // someone who reads the rules but can't change this one sees its value as words, not a dead field
  if (disabled)
    return (
      <div className="editor">
        <span className="ro-val">{settingWords(control, value, staff)}</span>
      </div>
    );
  let input;
  switch (control.kind) {
    case 'bool':
      input = (
        <label className="switch">
          <input
            id={id}
            type="checkbox"
            checked={draft === true}
            disabled={disabled}
            onChange={(e) => pick(e.target.checked)}
          />
          <span>{draft === true ? 'On' : 'Off'}</span>
        </label>
      );
      break;
    case 'select':
      input = (
        <select
          id={id}
          aria-label={label}
          className="field"
          disabled={disabled}
          value={String(draft)}
          onChange={(e) => {
            const opt = control.options.find(([v]) => String(v) === e.target.value);
            pick(opt ? opt[0] : e.target.value);
          }}
        >
          {control.options.map(([v, l]) => (
            <option key={String(v)} value={String(v)}>
              {l}
            </option>
          ))}
        </select>
      );
      break;
    case 'number':
      input = (
        <span className="row">
          <input
            id={id}
            aria-label={label}
            className="field num"
            type="number"
            min={control.min}
            max={control.max}
            disabled={disabled}
            value={String(draft ?? '')}
            onChange={(e) => setDraft(e.target.value === '' ? null : Number(e.target.value))}
          />
          <span className="muted small">{control.unit}</span>
        </span>
      );
      break;
    case 'money':
      input = (
        <span className="row">
          <span>$</span>
          <input
            id={id}
            aria-label={label}
            className="field num"
            inputMode="decimal"
            disabled={disabled}
            placeholder={control.none}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </span>
      );
      break;
    case 'text':
      input = (
        <input
          id={id}
          aria-label={label}
          className="field"
          type={control.type ?? 'text'}
          disabled={disabled}
          value={typeof draft === 'string' ? draft : ''}
          placeholder="Not set"
          onChange={(e) => setDraft(e.target.value === '' ? null : e.target.value)}
        />
      );
      break;
    case 'quietHours': {
      const q = draft as { start: string; end: string } | null;
      input = (
        <span className="row">
          <label className="switch">
            <input
              type="checkbox"
              aria-label={`${label}: on`}
              checked={!!q}
              disabled={disabled}
              onChange={(e) => pick(e.target.checked ? { start: '21:00', end: '08:00' } : null)}
            />
            <span>{q ? 'From' : 'None'}</span>
          </label>
          {q && (
            <>
              <input
                aria-label={`${label}: from`}
                className="field time"
                type="time"
                disabled={disabled}
                value={q.start}
                onChange={(e) => setDraft({ ...q, start: e.target.value })}
              />
              <span>to</span>
              <input
                aria-label={`${label}: to`}
                className="field time"
                type="time"
                disabled={disabled}
                value={q.end}
                onChange={(e) => setDraft({ ...q, end: e.target.value })}
              />
            </>
          )}
        </span>
      );
      break;
    }
    case 'workingHours': {
      const w = draft as { open: string; close: string; days: number[] };
      input = (
        <span className="row wrap">
          <input
            aria-label={`${label}: opens at`}
            className="field time"
            type="time"
            disabled={disabled}
            value={w.open}
            onChange={(e) => setDraft({ ...w, open: e.target.value })}
          />
          <span>to</span>
          <input
            aria-label={`${label}: closes at`}
            className="field time"
            type="time"
            disabled={disabled}
            value={w.close}
            onChange={(e) => setDraft({ ...w, close: e.target.value })}
          />
          <span className="days" role="group" aria-label={`${label}: days`}>
            {WEEKDAYS.map(([d, l]) => (
              <button
                key={d}
                type="button"
                className={`btn quiet${w.days.includes(d) ? ' on' : ''}`}
                aria-pressed={w.days.includes(d)}
                disabled={disabled}
                onClick={() =>
                  pick({
                    ...w,
                    days: w.days.includes(d) ? w.days.filter((x) => x !== d) : [...w.days, d].sort(),
                  })
                }
              >
                {l}
              </button>
            ))}
          </span>
        </span>
      );
      break;
    }
    case 'methods': {
      const on = draft as string[];
      input = (
        <span className="days" role="group" aria-label={label}>
          {SIGN_IN_METHODS.map(([m, l]) => (
            <button
              key={m}
              type="button"
              className={`btn quiet${on.includes(m) ? ' on' : ''}`}
              aria-pressed={on.includes(m)}
              disabled={disabled || (on.length === 1 && on.includes(m))}
              onClick={() => pick(toggleMethod(on, m))}
            >
              {l}
            </button>
          ))}
        </span>
      );
      break;
    }
    case 'financing':
      // BL-005: the lender, its link and the plans, saved together
      input = (
        <FinancingPlans
          id={id}
          value={draft as FinancingSetting}
          disabled={disabled}
          onChange={(v) => setDraft(v)}
        />
      );
      break;
    case 'paragraph':
      input = (
        <textarea
          id={id}
          aria-label={label}
          className="field"
          rows={4}
          value={typeof draft === 'string' ? draft : ''}
          onChange={(e) => setDraft(e.target.value)}
        />
      );
      break;
    case 'words':
      input = control.perLine ? (
        <textarea
          id={id}
          aria-label={label}
          className="field"
          rows={Math.min(control.max, 4)}
          value={text}
          placeholder="None"
          onChange={(e) => setText(e.target.value)}
        />
      ) : (
        <input
          id={id}
          aria-label={label}
          className="field"
          value={text}
          placeholder="None"
          onChange={(e) => setText(e.target.value)}
        />
      );
      break;
    case 'time':
      input = (
        <input
          id={id}
          aria-label={label}
          className="field time"
          type="time"
          value={typeof draft === 'string' ? draft : ''}
          onChange={(e) => setDraft(e.target.value)}
        />
      );
      break;
    case 'colour':
      // typed as #RRGGBB: the server checks it reads on light and dark, and says the nearest that does
      input = (
        <input
          id={id}
          aria-label={label}
          className="field"
          value={typeof draft === 'string' ? draft : ''}
          placeholder="#RRGGBB, or blank for Flowology’s own"
          onChange={(e) => setDraft(e.target.value.trim() === '' ? null : e.target.value.trim())}
        />
      );
      break;
    case 'overtime':
      input = (
        <span className="row wrap" role="group" aria-label={label}>
          {OVERTIME_PARTS.map(([k, words]) => (
            <label key={k} className="row">
              <input
                aria-label={`${label}: ${words}`}
                className="field num"
                inputMode="decimal"
                value={hours[k] ?? ''}
                onChange={(e) => setHours({ ...hours, [k]: e.target.value })}
              />
              <span className="muted small">{words}</span>
            </label>
          ))}
        </span>
      );
      break;
    case 'twoFactor': {
      const t = draft as TwoFactor;
      input = (
        <span className="row wrap">
          <select
            id={id}
            aria-label={label}
            className="field"
            value={t.mode}
            onChange={(e) => pick({ ...t, mode: e.target.value as TwoFactor['mode'] })}
          >
            {TWO_FACTOR_MODES.map(([m, l]) => (
              <option key={m} value={m}>
                {l}
              </option>
            ))}
          </select>
          {t.mode === 'roles' && (
            <span className="days" role="group" aria-label={`${label}: the roles`}>
              {[
                ...TWO_FACTOR_ROLES,
                // a company's own role already in the list keeps its place
                ...t.roles
                  .filter((r) => !TWO_FACTOR_ROLES.some(([k]) => k === r))
                  .map((r) => [r, r] as const),
              ].map(([r, l]) => (
                <button
                  key={r}
                  type="button"
                  className={`btn quiet${t.roles.includes(r) ? ' on' : ''}`}
                  aria-pressed={t.roles.includes(r)}
                  onClick={() =>
                    pick({
                      ...t,
                      roles: t.roles.includes(r) ? t.roles.filter((x) => x !== r) : [...t.roles, r],
                    })
                  }
                >
                  {l}
                </button>
              ))}
            </span>
          )}
          {t.mode !== 'off' && (
            <label className="switch">
              <input
                type="checkbox"
                aria-label={`${label}: authenticator app only`}
                checked={t.appOnly}
                onChange={(e) => pick({ ...t, appOnly: e.target.checked })}
              />
              <span>Authenticator app only</span>
            </label>
          )}
        </span>
      );
      break;
    }
    case 'answerHours': {
      // the office's hours it answers outside, or always
      const a = draft as AnswerHours;
      input = (
        <span className="row wrap">
          <select
            id={id}
            aria-label={label}
            className="field"
            value={a.mode}
            onChange={(e) => pick({ ...a, mode: e.target.value as AnswerHours['mode'] })}
          >
            <option value="after_hours">Outside office hours</option>
            <option value="always">Always</option>
          </select>
          {a.mode === 'after_hours' && (
            <>
              <input
                aria-label={`${label}: the office opens`}
                className="field time"
                type="time"
                value={a.open}
                onChange={(e) => setDraft({ ...a, open: e.target.value })}
              />
              <span>to</span>
              <input
                aria-label={`${label}: the office closes`}
                className="field time"
                type="time"
                value={a.close}
                onChange={(e) => setDraft({ ...a, close: e.target.value })}
              />
              <span className="days" role="group" aria-label={`${label}: the office's days`}>
                {WEEKDAYS.map(([d, l]) => (
                  <button
                    key={d}
                    type="button"
                    className={`btn quiet${a.days.includes(d) ? ' on' : ''}`}
                    aria-pressed={a.days.includes(d)}
                    onClick={() =>
                      pick({
                        ...a,
                        days: a.days.includes(d) ? a.days.filter((x) => x !== d) : [...a.days, d].sort(),
                      })
                    }
                  >
                    {l}
                  </button>
                ))}
              </span>
            </>
          )}
        </span>
      );
      break;
    }
    case 'alertTo':
      input = (
        <select
          id={id}
          aria-label={label}
          className="field"
          disabled={disabled}
          value={String(draft)}
          onChange={(e) => pick(e.target.value)}
        >
          <option value="managers">The owner and managers</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
        </select>
      );
      break;
  }
  return (
    <div
      className="editor"
      // Blueprint: typing saves when the field is left, or on Enter (a textarea's Enter is a new line);
      // Escape puts back the saved value, so leaving the field then saves nothing
      onBlur={
        auto
          ? (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commit(out);
            }
          : undefined
      }
      onKeyDown={
        auto
          ? (e) => {
              if (e.key === 'Escape') putBack();
              else if (e.key === 'Enter' && e.target instanceof HTMLInputElement) commit(out);
            }
          : undefined
      }
    >
      {input}
      {dirty && !disabled && !auto && (
        <button className="btn primary" disabled={busy} onClick={() => void save()}>
          Save
        </button>
      )}
      {asking && (
        <p className="bp-ask small" role="alert">
          <span>{asking.words}</span>
          <button
            className="btn soft"
            disabled={busy}
            onClick={() => void save(asking.value).then(() => setAsking(null))}
          >
            Apply
          </button>
          <button className="btn quiet" onClick={putBack}>
            Cancel
          </button>
        </p>
      )}
      {onClear && !dirty && (
        <button
          className="btn soft"
          disabled={busy}
          onClick={() =>
            void onClear().catch((e: unknown) => {
              setError(message(e));
            })
          }
        >
          {auto ? 'Back to the company’s value' : 'Use company value'}
        </button>
      )}
      {saved && (
        <span className="chip ok" role="status">
          Saved
        </span>
      )}
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Blueprint: whether a value is still Flowology's default, or the company's own with the way back to
 * the default (settings.clear). A rule with consequences asks before it goes back, as it does to change.
 */
function RowSource({
  rule,
  row,
  canEdit,
  onSaved,
}: {
  rule: Rule;
  row: SettingRow;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const back = async () => {
    setAsking(false);
    try {
      await client.call('settings.clear', { key: rule.key });
      setError(null);
      await onSaved();
    } catch (e) {
      setError(message(e));
    }
  };
  if (row.source === 'invalid') return <p className="bp-src small error">It can’t be read. Set it again.</p>;
  if (row.source === 'default') return <p className="bp-src small muted">Flowology’s default</p>;
  return (
    <p className="bp-src small">
      <span>Changed</span>
      {canEdit && !asking && (
        <button className="btn link" onClick={() => (rule.confirm ? setAsking(true) : void back())}>
          Back to the default
        </button>
      )}
      {asking && (
        <span className="bp-ask" role="alert">
          <span>Put back Flowology’s default?</span>
          <button className="btn soft" onClick={() => void back()}>
            Apply
          </button>
          <button className="btn quiet" onClick={() => setAsking(false)}>
            Cancel
          </button>
        </span>
      )}
      {error && <span className="error">{error}</span>}
    </p>
  );
}

function useFound(key: string) {
  const ref = useRef<HTMLLIElement>(null);
  const on = useFoundSetting(key, () => ref.current);
  return { ref, on };
}
